import './map.css';
import MapCard from './MapCard';
import MapNeonDefs from './MapNeonDefs';
import { BOX_H, BOX_W } from './cardBody';
import { compassPoint, splitDistance } from './geo';

export interface CompassViewProps {
  /** Compass bearing to the target, degrees from true north. */
  bearingDeg: number;
  /** Arrow angle relative to where the player faces, or null if heading is unknown. */
  relativeDeg: number | null;
  distanceM: number;
  units: 'm' | 'ft';
  label: string;
  /** Distance inside which the client waits for server confirmation. */
  arrivalRadiusM: number;
  /** Bump this to replay the one-shot reveal. */
  revealKey: number;
}

// Body box comes from cardBody.ts (shared with SecretMapView, so the two
// views can never drift apart on the card's own metrics).
const RING_CX = BOX_W / 2;
// Vertical rhythm on the 8 px grid, sized to fit the 261px-tall body box
// (Jason, 2026-09-29: the map card no longer claims the whole screen, since
// the mic/keyboard chips stay on screen during a walk): ring top at 8, ring
// bottom at 128, a 24 px gap to the hero cap line, hero baseline at 182,
// then 30 px to the cardinal line — still inside the box, with room to
// spare below it.
export const RING_CY = 64;
export const RING_R = 56;
export const HERO_BASELINE = 182;
export const SUB_BASELINE = 212;
// The arrow's own geometry, in ratios of RING_R (matched to the original
// 112px-ring arrow: tip at 0.7R, base spread at 0.357R, notch at 0.107R,
// waist at 0.286R), so it keeps its shape as the ring resizes.
export const ARROW_TIP = RING_R * 0.7;
const ARROW_SPREAD = RING_R * 0.357;
const ARROW_NOTCH = RING_R * 0.107;
const ARROW_WAIST = RING_R * 0.286;
const ARRIVED_R = RING_R * 0.339;

/**
 * Compass + distance: the "Go" cue from the GDD's Traverse verb, "the
 * directional cue the display shows for a beat". One arrow, one number, one
 * name. When heading is unknown the arrow points by compass bearing with
 * north up and says so in grey. Lives inside the same dossier card frame as
 * the main glasses screen (MapCard), bolder and larger per Jason's
 * 2026-09-28 "bolder, use more space" direction.
 */
export default function CompassView(props: CompassViewProps) {
  const { bearingDeg, relativeDeg, distanceM, units, label, arrivalRadiusM, revealKey } = props;
  const nearby = distanceM <= arrivalRadiusM;
  const angle = relativeDeg ?? bearingDeg;
  const headingKnown = relativeDeg !== null;
  const [heroValue, heroUnit] = splitDistance(distanceM, units);
  return (
    <div className="mu-map-view mu-compass" key={revealKey} data-view="compass" data-nearby={nearby}>
      <MapCard status={label}>
        <svg viewBox={`0 0 ${BOX_W} ${BOX_H}`} aria-label="compass cue">
          <MapNeonDefs />

          {/* Unlit ring, no glow. Ticks dropped per the style guide's 2026-09-28
              ruling 3: thin decoration that would vanish on the display. */}
          <circle cx={RING_CX} cy={RING_CY} r={RING_R} className="mu-ring" />

          {!nearby ? (
            <g transform={`rotate(${angle.toFixed(1)} ${RING_CX} ${RING_CY})`}>
            <g className="mu-reveal-arrow" filter="url(#mu-neon)">
              <path
                d={`M${RING_CX} ${RING_CY - ARROW_TIP} L${RING_CX + ARROW_SPREAD} ${RING_CY - ARROW_NOTCH} L${RING_CX} ${RING_CY - ARROW_WAIST} L${RING_CX - ARROW_SPREAD} ${RING_CY - ARROW_NOTCH} Z`}
                className="mu-arrow"
              />
            </g>
            </g>
          ) : (
            <g className="mu-reveal-arrow" filter="url(#mu-neon)">
              <circle cx={RING_CX} cy={RING_CY} r={ARRIVED_R} fill="none" stroke="var(--mu-lit)" strokeWidth="9" />
            </g>
          )}

          {nearby ? (
            // Proximity is advisory; only the server can complete this step.
            <text x={RING_CX} y={HERO_BASELINE} className="mu-t-cardinal" textAnchor="middle">
              CHECKING LOCATION
            </text>
          ) : (
            <text x={RING_CX} y={HERO_BASELINE} textAnchor="middle">
              <tspan className="mu-t-hero">{heroValue}</tspan>
              <tspan className="mu-t-hero-unit" dx="6">
                {heroUnit}
              </tspan>
            </text>
          )}
          <text x={RING_CX} y={SUB_BASELINE} className="mu-t-cardinal" textAnchor="middle">
            {nearby ? '' : headingKnown ? compassPoint(bearingDeg) : `north up · ${compassPoint(bearingDeg)}`}
          </text>
        </svg>
      </MapCard>
    </div>
  );
}
